import {ActionBase, ActionWithPayload} from '../../helpers/reduxHelpers';
import {
  electronWindowBridgeHas,
  withElectronWindowBridgeHaving,
} from '../../services/electron/electronBridgeOutbound';
import {ThunkAsyncAction} from '../appThunk';
import {ElectronState} from './electronState';

/*
 * Action types.
 */

export enum ElectronActionTypesEnum {
  INITIALIZED = '@@electron/INITIALIZED',
  DID_OPEN_APPLICATION_MENU = '@@electron/DID_OPEN_APPLICATION_MENU',
  DID_CLOSE_APPLICATION_MENU = '@@electron/DID_CLOSE_APPLICATION_MENU',
  DID_MAXIMIZE = '@@electron/DID_MAXIMIZE',
  DID_UNMAXIMIZE = '@@electron/DID_UNMAXIMIZE',
  DID_ENTER_FULL_SCREEN = '@@electron/DID_ENTER_FULL_SCREEN',
  DID_LEAVE_FULL_SCREEN = '@@electron/DID_LEAVE_FULL_SCREEN',
  ZOOM_FACTOR_UPDATED = '@@electron/ZOOM_FACTOR_UPDATED',
}

/*
 * Actions.
 */

export interface ElectronInitializedAction extends ActionWithPayload<ElectronState> {
  type: ElectronActionTypesEnum.INITIALIZED;
}

export interface ElectronDidOpenApplicationMenuAction extends ActionBase {
  type: ElectronActionTypesEnum.DID_OPEN_APPLICATION_MENU;
}

export interface ElectronDidCloseApplicationMenuAction extends ActionBase {
  type: ElectronActionTypesEnum.DID_CLOSE_APPLICATION_MENU;
}

export interface ElectronDidMaximizeAction extends ActionBase {
  type: ElectronActionTypesEnum.DID_MAXIMIZE;
}

export interface ElectronDidUnmaximizeAction extends ActionBase {
  type: ElectronActionTypesEnum.DID_UNMAXIMIZE;
}

export interface ElectronDidEnterFullScreenAction extends ActionBase {
  type: ElectronActionTypesEnum.DID_ENTER_FULL_SCREEN;
}

export interface ElectronDidLeaveFullScreenAction extends ActionBase {
  type: ElectronActionTypesEnum.DID_LEAVE_FULL_SCREEN;
}

export interface ElectronZoomFactorUpdatedAction extends ActionWithPayload<ElectronZoomFactorPayload> {
  type: ElectronActionTypesEnum.ZOOM_FACTOR_UPDATED;
}

interface ElectronZoomFactorPayload {
  zoomFactor: number;
}

export type ElectronAction =
  | ElectronInitializedAction
  | ElectronDidOpenApplicationMenuAction
  | ElectronDidCloseApplicationMenuAction
  | ElectronDidMaximizeAction
  | ElectronDidUnmaximizeAction
  | ElectronDidEnterFullScreenAction
  | ElectronDidLeaveFullScreenAction
  | ElectronZoomFactorUpdatedAction;

/*
 * Action creators.
 */

export function electronInitializeAsyncAction(): ThunkAsyncAction {
  return async (dispatch) => {
    const initialState: ElectronState = {
      canSetMacOsTrafficLightPosition: electronWindowBridgeHas('setTrafficLightPosition'),
      canHideWindowsTitleBar: electronWindowBridgeHas('hideWindowsTitleBar'),
      isApplicationMenuOpen: false,
      isMaximized: false,
      isFullScreen: false,
      zoomFactor: undefined,
    };
    dispatch({
      type: ElectronActionTypesEnum.INITIALIZED,
      payload: initialState,
    });

    withElectronWindowBridgeHaving('isMaximized', async (bridge) => {
      const isMaximized = await bridge.isMaximized();

      if (isMaximized) {
        dispatch(electronDidMaximizeAction());
      } else {
        dispatch(electronDidUnmaximizeAction());
      }
    });

    withElectronWindowBridgeHaving('isFullScreen', async (bridge) => {
      const isFullScreen = await bridge.isFullScreen();

      if (isFullScreen) {
        dispatch(electronDidEnterFullScreenAction());
      } else {
        dispatch(electronDidLeaveFullScreenAction());
      }
    });

    withElectronWindowBridgeHaving('getZoomFactor', async (bridge) => {
      const zoomFactor = await bridge.getZoomFactor();
      dispatch(electronZoomFactorUpdatedAction(zoomFactor));
    });

    withElectronWindowBridgeHaving(['onApplicationMenuOpen', 'onApplicationMenuClose'], (bridge) => {
      bridge.onApplicationMenuOpen(() => dispatch(electronDidOpenApplicationMenuAction()));
      bridge.onApplicationMenuClose(() => dispatch(electronDidCloseApplicationMenuAction()));
    });

    withElectronWindowBridgeHaving(['onMaximize', 'onUnmaximize'], (bridge) => {
      bridge.onMaximize(() => dispatch(electronDidMaximizeAction()));
      bridge.onUnmaximize(() => dispatch(electronDidUnmaximizeAction()));
    });
  };
}

export function electronDidOpenApplicationMenuAction(): ElectronDidOpenApplicationMenuAction {
  return {type: ElectronActionTypesEnum.DID_OPEN_APPLICATION_MENU};
}

export function electronDidCloseApplicationMenuAction(): ElectronDidCloseApplicationMenuAction {
  return {type: ElectronActionTypesEnum.DID_CLOSE_APPLICATION_MENU};
}

export function electronDidMaximizeAction(): ElectronDidMaximizeAction {
  return {type: ElectronActionTypesEnum.DID_MAXIMIZE};
}

export function electronDidUnmaximizeAction(): ElectronDidUnmaximizeAction {
  return {type: ElectronActionTypesEnum.DID_UNMAXIMIZE};
}

export function electronDidEnterFullScreenAction(): ElectronDidEnterFullScreenAction {
  return {type: ElectronActionTypesEnum.DID_ENTER_FULL_SCREEN};
}

export function electronDidLeaveFullScreenAction(): ElectronDidLeaveFullScreenAction {
  return {type: ElectronActionTypesEnum.DID_LEAVE_FULL_SCREEN};
}

export function electronZoomFactorUpdatedAction(zoomFactor: number): ElectronZoomFactorUpdatedAction {
  return {type: ElectronActionTypesEnum.ZOOM_FACTOR_UPDATED, payload: {zoomFactor}};
}
